import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ContactModal from "../components/ContactModal";
import SEOHead from "../components/SEOHead";
import { useSpamSchutz, Honigtopf } from "../components/SpamSchutz";
import { 
  ArrowRight, 
  ExternalLink, 
  Mail, 
  Star,
  TrendingUp,
  Users,
  Globe,
  Shield,
  CheckCircle2,
  AlertCircle,
  Building2,
  Factory,
  Banknote,
  Car,
  Truck,
  Megaphone,
  BarChart3,
  Server,
  HeadphonesIcon,
  Calculator,
  Database,
  Crown,
  Heart,
  GraduationCap,
  Loader2
} from "lucide-react";

const AIUseCases = () => {
  const { t } = useLanguage();
  const { honigtopf, spamFelder } = useSpamSchutz();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const seoData = {
    title: "KI Fallstudien - Erfolgreiche AI-Projekte | BROVKO",
    description: "Entdecken Sie erfolgreiche KI-Implementierungen aus verschiedenen Branchen. Echte Fallstudien zeigen den ROI unserer AI-Transformationsprojekte.",
    keywords: "KI Fallstudien, AI Use Cases, KI Erfolgsgeschichten, AI ROI",
    canonicalUrl: "/ki-fallstudien",
    ogTitle: "KI Fallstudien - Erfolgreiche AI-Projekte",
    ogDescription: "Echte Erfolgsgeschichten aus unseren KI-Transformationsprojekten."
  };

  // Modal handlers
  const openContactModal = (buttonContext = null, buttonText = null) => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  // Newsletter form state
  const [newsletterData, setNewsletterData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });
  const [newsletterStatus, setNewsletterStatus] = useState('idle'); // idle, submitting, success, error
  const [newsletterErrors, setNewsletterErrors] = useState({});

  // Newsletter form handlers
  const handleNewsletterInputChange = (e) => {
    const { name, value } = e.target;
    setNewsletterData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (newsletterErrors[name]) {
      setNewsletterErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateNewsletterForm = () => {
    const errors = {};
    
    if (!newsletterData.firstName.trim()) {
      errors.firstName = t('errors.firstName');
    }
    
    if (!newsletterData.lastName.trim()) {
      errors.lastName = t('errors.lastName');
    }
    
    if (!newsletterData.email.trim()) {
      errors.email = t('errors.email');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newsletterData.email)) {
      errors.email = t('errors.emailInvalid');
    }
    
    setNewsletterErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateNewsletterForm()) {
      return;
    }
    
    setNewsletterStatus('submitting');
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/general`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(await spamFelder()),
          firstName: newsletterData.firstName,
          lastName: newsletterData.lastName,
          email: newsletterData.email,
          interests: ["AI Use Cases", "Technology", "Innovation"] // Default interests for this form
        }),
      });

      const result = await response.json();

      if (result.success) {
        setNewsletterStatus('success');
        setNewsletterData({ firstName: '', lastName: '', email: '' });
        setNewsletterErrors({});
        
        // Auto-hide success message after 7 seconds
        setTimeout(() => {
          setNewsletterStatus('idle');
        }, 7000);
      } else {
        // Handle specific errors from backend
        if (result.message) {
          // Subscription error handled in UI
          // You could show specific error messages here if needed
        }
        setNewsletterStatus('error');
        
        // Auto-hide error after 5 seconds
        setTimeout(() => {
          setNewsletterStatus('idle');
        }, 5000);
      }
      
    } catch (error) {
      // Network error handled in UI
      setNewsletterStatus('error');
      
      // Auto-hide error after 5 seconds
      setTimeout(() => {
        setNewsletterStatus('idle');
      }, 5000);
    }
  };

  const industries = [
    { key: 'retail', icon: Building2, color: 'blue' },
    { key: 'manufacturing', icon: Factory, color: 'green' },
    { key: 'banking', icon: Banknote, color: 'yellow' },
    { key: 'insurance', icon: Shield, color: 'purple' },
    { key: 'pe', icon: Crown, color: 'indigo' },
    { key: 'automotive', icon: Car, color: 'red' },
    { key: 'logistics', icon: Truck, color: 'orange' },
    { key: 'marketing', icon: Megaphone, color: 'pink' },
    { key: 'sales', icon: TrendingUp, color: 'teal' },
    { key: 'it', icon: Server, color: 'gray' },
    { key: 'service', icon: HeadphonesIcon, color: 'cyan' },
    { key: 'controlling', icon: Calculator, color: 'emerald' },
    { key: 'datascience', icon: Database, color: 'violet' },
    { key: 'healthcare', icon: Heart, color: 'rose' },
    { key: 'education', icon: GraduationCap, color: 'amber' }
  ];

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-500/15 text-blue-300 border-blue-400/25',
      green: 'bg-green-500/15 text-green-300 border-green-400/25',
      yellow: 'bg-yellow-500/15 text-yellow-300 border-yellow-400/25',
      purple: 'bg-purple-500/15 text-purple-300 border-purple-400/25',
      indigo: 'bg-indigo-500/15 text-indigo-300 border-indigo-400/25',
      red: 'bg-red-500/15 text-red-300 border-red-400/25',
      orange: 'bg-orange-500/15 text-orange-300 border-orange-400/25',
      pink: 'bg-pink-500/15 text-pink-300 border-pink-400/25',
      teal: 'bg-brand/10 text-brand-300 border border-brand-400/25 border-brand-400/25',
      gray: 'bg-surface-raised text-ink-muted border-hairline',
      cyan: 'bg-brand/15 text-brand-300 border-brand-400/25',
      emerald: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/25',
      violet: 'bg-violet-100 text-violet-700 border-violet-200',
      rose: 'bg-rose-100 text-rose-700 border-rose-200',
      amber: 'bg-amber-500/15 text-amber-300 border-amber-200'
    };
    return colorMap[color] || colorMap.blue;
  };

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-surface-sunken">
      <SEOHead {...seoData} />
      <Header />
      
      {/* Full Screen Success Modal for General Newsletter */}
      {newsletterStatus === 'success' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-2xl p-12 mx-4 max-w-2xl w-full text-center shadow-2xl">
            <CheckCircle2 className="h-24 w-24 text-green-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-ink mb-4">
              Erfolgreich angemeldet!
            </h2>
            <p className="text-xl text-ink-muted mb-8 leading-relaxed">
              Vielen Dank für das Abonnement unseres Newsletters! Sie erhalten in Kürze eine Bestätigungs-E-Mail.
            </p>
            <Button 
              onClick={() => setNewsletterStatus('idle')}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-lg"
            >
              Schließen
            </Button>
          </div>
        </div>
      )}
      
      {/* Hero Section */}
      <section className="pt-40 pb-10 bg-gradient-to-br from-slate-900 to-teal-900">
        <div className="container mx-auto px-4">
          <div className=" text-white">
            <h1 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] mb-4">
              {t('usecases.hero.title')}
            </h1>
            <p className="text-base md:text-lg text-ink-muted max-w-4xl mx-auto">
              {t('usecases.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* AICaseHub Section */}
      <section className="py-20 md:py-28 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              
              {/* Left Column - Content + Newsletter */}
              <div className="space-y-8">
                {/* AICaseHub Content */}
                <div>
                  <div className="inline-flex items-center bg-brand/15 text-brand-300 px-3 py-1.5 rounded-full text-xs font-semibold mb-4">
                    <Star className="h-3 w-3 mr-1.5" />
                    {t('usecases.aicasehub.badge')}
                  </div>
                  
                  <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-4">
                    {t('usecases.aicasehub.title')}
                  </h2>
                  
                  <p className="text-base text-ink-muted mb-4">
                    {t('usecases.aicasehub.subtitle')}
                  </p>
                  
                  <p className="text-sm text-ink-muted mb-6 leading-relaxed">
                    {t('usecases.aicasehub.description')}
                  </p>

                  <div className="space-y-2 mb-6">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                      <p className="text-ink-muted text-sm">{t('usecases.aicasehub.benefit1')}</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                      <p className="text-ink-muted text-sm">{t('usecases.aicasehub.benefit2')}</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                      <p className="text-ink-muted text-sm">{t('usecases.aicasehub.benefit3')}</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-brand-400 mt-0.5 flex-shrink-0" />
                      <p className="text-ink-muted text-sm">{t('usecases.aicasehub.benefit4')}</p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    <a
                      href="https://aicasehub.io"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button className="bg-brand hover:bg-brand-600 text-ink-onBrand px-6 py-2 text-sm">
                        {t('usecases.aicasehub.cta')}
                        <ExternalLink className="ml-2 h-3 w-3" />
                      </Button>
                    </a>
                  </div>

                  <p className="text-xs text-ink-faint mb-8">
                    {t('usecases.aicasehub.newsletter.description')}
                  </p>
                </div>

                {/* Newsletter Form */}
                <div>
                  <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-3">
                    {t('usecases.newsletter.title')}
                  </h3>
                  <p className="text-ink-muted text-sm mb-4">
                    {t('usecases.newsletter.subtitle')}
                  </p>

                  <Card className="border-0 shadow-lg bg-surface-sunken">
                    <CardContent className="p-4">
                      {newsletterStatus === 'error' ? (
                        <div className="text-center py-6">
                          <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
                          <h4 className="text-xl font-bold text-ink mb-2">
                            Anmeldung fehlgeschlagen
                          </h4>
                          <p className="text-ink-muted mb-6">
                            Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.
                          </p>
                          <Button 
                            onClick={() => setNewsletterStatus('idle')}
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            Erneut versuchen
                          </Button>
                        </div>
                      ) : (
                        <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                          <Honigtopf {...honigtopf} />
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label htmlFor="firstName" className="block text-sm font-medium text-ink-muted mb-1">
                                {t('usecases.newsletter.firstName')} *
                              </label>
                              <Input
                                id="firstName"
                                name="firstName"
                                type="text"
                                value={newsletterData.firstName}
                                onChange={handleNewsletterInputChange}
                                className={newsletterErrors.firstName ? "border-red-400" : ""}
                                required
                              />
                              {newsletterErrors.firstName && (
                                <p className="text-red-400 text-xs mt-1">{newsletterErrors.firstName}</p>
                              )}
                            </div>
                            
                            <div>
                              <label htmlFor="lastName" className="block text-sm font-medium text-ink-muted mb-1">
                                {t('usecases.newsletter.lastName')} *
                              </label>
                              <Input
                                id="lastName"
                                name="lastName"
                                type="text"
                                value={newsletterData.lastName}
                                onChange={handleNewsletterInputChange}
                                className={newsletterErrors.lastName ? "border-red-400" : ""}
                                required
                              />
                              {newsletterErrors.lastName && (
                                <p className="text-red-400 text-xs mt-1">{newsletterErrors.lastName}</p>
                              )}
                            </div>
                          </div>

                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-ink-muted mb-1">
                              {t('usecases.newsletter.email')} *
                            </label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={newsletterData.email}
                              onChange={handleNewsletterInputChange}
                              className={newsletterErrors.email ? "border-red-400" : ""}
                              required
                            />
                            {newsletterErrors.email && (
                              <p className="text-red-400 text-xs mt-1">{newsletterErrors.email}</p>
                            )}
                          </div>

                          <Button
                            type="submit"
                            className="w-full bg-gradient-to-r from-blue-600 to-brand-600 hover:from-blue-700 hover:to-teal-700 text-white py-3"
                            disabled={newsletterStatus === 'submitting'}
                          >
                            {newsletterStatus === 'submitting' ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {t('usecases.newsletter.submitting')}
                              </>
                            ) : (
                              <>
                                <Mail className="mr-2 h-4 w-4" />
                                {t('usecases.newsletter.subscribe')}
                              </>
                            )}
                          </Button>

                          <p className="text-center text-xs text-ink-faint mt-3">
                            {t('usecases.newsletter.privacy')}
                          </p>
                        </form>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Right Column - Screenshot */}
              <div>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-teal-400 to-blue-500 rounded-2xl transform rotate-3"></div>
                  <div className="relative bg-surface p-4 rounded-2xl shadow-2xl">
                    <img 
                      src="/assets/images/spinoff-aiwizz.png"
                      alt="AICaseHub.io Screenshot"
                      className="w-full h-auto rounded-xl shadow-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 md:py-28 bg-surface-sunken">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-8">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-3">
              {t('usecases.industries.title')}
            </h2>
            <p className="text-base text-ink-muted max-w-[62ch]">
              {t('usecases.industries.subtitle')}
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {industries.map((industry) => {
                const IconComponent = industry.icon;
                return (
                  <div 
                    key={industry.key}
                    className={`p-3 rounded-xl border text-center hover:shadow-md transition-all duration-300 ${getColorClasses(industry.color)}`}
                  >
                    <IconComponent className="h-6 w-6 mx-auto mb-2" />
                    <p className="font-medium text-xs">
                      {t(`usecases.industries.${industry.key}`)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* BROVKO Case Studies Section */}
      <section className="py-20 md:py-28 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-[64ch] mb-8">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-3">
              {t('usecases.brovko.title')}
            </h2>
            <p className="text-base text-ink-muted max-w-[62ch]">
              {t('usecases.brovko.subtitle')}
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-4">
              
              {/* Case Study 1 */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-5">
                  <div className="mb-4">
                    <div className="inline-flex items-center bg-blue-500/15 text-blue-300 px-2 py-0.5 rounded-full text-xs font-medium mb-2">
                      {t('usecases.case1.industry')}
                    </div>
                    <h3 className="text-lg font-bold text-ink mb-2">
                      {t('usecases.case1.title')}
                    </h3>
                    <p className="text-ink-muted text-sm mb-4 leading-relaxed">
                      {t('usecases.case1.description')}
                    </p>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-400" />
                      <span className="font-semibold text-green-400 text-sm">{t('usecases.case1.result1')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-400" />
                      <span className="font-semibold text-green-400 text-sm">{t('usecases.case1.result2')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-400" />
                      <span className="font-semibold text-green-400 text-sm">{t('usecases.case1.result3')}</span>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-hairline">
                    <p className="text-xs text-ink-faint mb-1 font-medium">Technologie:</p>
                    <p className="text-xs text-ink-muted">{t('usecases.case1.technology')}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Case Study 2 */}
              <Card className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-5">
                  <div className="mb-4">
                    <div className="inline-flex items-center bg-green-500/15 text-green-300 px-2 py-0.5 rounded-full text-xs font-medium mb-2">
                      {t('usecases.case2.industry')}
                    </div>
                    <h3 className="text-lg font-bold text-ink mb-2">
                      {t('usecases.case2.title')}
                    </h3>
                    <p className="text-ink-muted text-sm mb-4 leading-relaxed">
                      {t('usecases.case2.description')}
                    </p>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-blue-400" />
                      <span className="font-semibold text-blue-400 text-sm">{t('usecases.case2.result1')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-400" />
                      <span className="font-semibold text-blue-400 text-sm">{t('usecases.case2.result2')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Factory className="h-4 w-4 text-blue-400" />
                      <span className="font-semibold text-blue-400 text-sm">{t('usecases.case2.result3')}</span>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-hairline">
                    <p className="text-xs text-ink-faint mb-1 font-medium">Technologie:</p>
                    <p className="text-xs text-ink-muted">{t('usecases.case2.technology')}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-r from-slate-900 to-teal-900">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3">
              {t('usecases.cta.title')}
            </h2>
            <p className="text-base text-ink-muted mb-6 max-w-[62ch]">
              {t('usecases.cta.subtitle')}
            </p>
            <Button 
              className="bg-brand hover:bg-brand-600 text-ink-onBrand px-6 py-3 text-sm"
              onClick={() => openContactModal("CTA Section", t('usecases.cta.button'))}
            >
              {t('usecases.cta.button')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Contact Modal */}
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={closeContactModal}
        pageSource="AI Use Cases"
        buttonContext="AI Use Cases CTA"
        buttonText="Kostenloses Beratungsgespräch buchen"
      />
    </div>
  );
};

export default AIUseCases;